import { useSuspenseQuery } from "@apollo/client/react";
import { Loader2, TriangleAlert } from "lucide-react";
import { Suspense, useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { DetailSubHeader } from "~/common/components";
import {
  GetRecordComposerDefinitionsDocument,
  type GetRecordComposerDefinitionsQuery,
  type GetRecordComposerDefinitionsQueryVariables,
} from "~/common/gqls";
import { BatchChangeRequestsJobType, type MutationRefetchQueries } from "~/common/types";
import { DetailLoading } from "~/components/loading";
import {
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui";
import { useAuth } from "~/features/authn/context";
import {
  GetBatchChangeRequestsJobDocument,
  type GetBatchChangeRequestsJobQuery,
  type GetBatchChangeRequestsJobQueryVariables,
} from "~/features/dataImportProjects/gqls";
import { useBatchChangeRequestsJobForm } from "~/features/dataImportProjects/hooks/useBatchChangeRequestsJobForm";
import { useEvidenceSetPropertySchemaSet } from "~/features/dataImportProjects/hooks/useEvidenceSetPropertySchemaSet";
import type { BatchChangeRequestJobsFormData } from "~/features/dataImportProjects/types";
import { useTenant } from "~/features/tenant/context";
import { BatchChangeRequestsComposerField } from "./BatchChangeRequestsComposerField";
import { EntityComposerEditForm, RelationComposerEditForm } from "./ComposerEditForm";
import { JobTypeLabel } from "./JobTypeLabel";

// s3_script is no longer offered for new BCRJobs; it remains in the enum for read-only display of historical records.
const batchChangeRequestsJobTypes = [
  BatchChangeRequestsJobType.EntityComposer,
  BatchChangeRequestsJobType.RelationComposer,
];

interface BatchChangeRequestsJobFormProps {
  importProjectId: string;
  evidenceSetId: string;
  onSubmit: () => void;
  refetchQueriesOnEdit: MutationRefetchQueries;
  batchChangeRequestsJobId: string | undefined;
  formId?: string;
}

export function BatchChangeRequestsJobForm({
  importProjectId,
  evidenceSetId,
  onSubmit,
  refetchQueriesOnEdit,
  batchChangeRequestsJobId,
  formId,
}: BatchChangeRequestsJobFormProps) {
  const { t } = useTranslation();
  const [jobType, setJobType] = useState<BatchChangeRequestsJobType>(BatchChangeRequestsJobType.EntityComposer);
  const [addFormOpen, setAddFormOpen] = useState(false);
  const { tenantId } = useTenant();
  if (!tenantId) {
    throw new Error("Tenant ID is required");
  }

  const methods = useForm<BatchChangeRequestJobsFormData>();
  const { control, handleSubmit, setValue, reset, register } = methods;

  const { submit, processing } = useBatchChangeRequestsJobForm({
    refetchQueriesOnEdit,
    onSuccess: onSubmit,
    batchChangeRequestsJobId,
    importProjectId,
  });

  const { data } = useSuspenseQuery<GetBatchChangeRequestsJobQuery, GetBatchChangeRequestsJobQueryVariables>(
    GetBatchChangeRequestsJobDocument,
    {
      variables: { tenantId, id: batchChangeRequestsJobId ?? "" },
      skip: !batchChangeRequestsJobId,
    },
  );

  const { data: composerData, refetch: refetchComposers } = useSuspenseQuery<
    GetRecordComposerDefinitionsQuery,
    GetRecordComposerDefinitionsQueryVariables
  >(GetRecordComposerDefinitionsDocument, {
    variables: { tenantId, dataImportProjectId: importProjectId },
  });

  useEffect(() => {
    if (!data) {
      return;
    }

    const job = data.batchChangeRequestsJobs[0];
    if (job) {
      const jobTypeId = (job.jobTypeId as BatchChangeRequestsJobType) ?? BatchChangeRequestsJobType.EntityComposer;
      setJobType(jobTypeId);
      reset({
        name: job.name ?? "",
        jobType: jobTypeId,
        recordComposerDefinitionIds: job.batchChangeRequestsJobRecordComposerDefinitions.map(
          (c) => c.recordComposerDefinition.id,
        ),
      });
    }
  }, [data]);

  const { user } = useAuth();
  if (!user) {
    throw Error("User does not exist!");
  }

  const { propertySchemaSet: evidenceSchemaSet } = useEvidenceSetPropertySchemaSet({ evidenceSetId });
  const hasEvidenceSchemaSet = (evidenceSchemaSet?.propertySchemaAssociations.length ?? 0) > 0;

  const job = batchChangeRequestsJobId ? data?.batchChangeRequestsJobs[0] : undefined;
  if (batchChangeRequestsJobId && !job) {
    throw Error("Job does not exist!");
  }

  const defaultComposerIds =
    job?.batchChangeRequestsJobRecordComposerDefinitions.map((c) => c.recordComposerDefinition.id) ?? [];
  const isEntity = jobType === BatchChangeRequestsJobType.EntityComposer;
  const isRelation = jobType === BatchChangeRequestsJobType.RelationComposer;

  const entityComposers =
    composerData?.entityRecordComposerDefinitions
      .map((er) => er.recordComposerDefinition)
      .filter((rcd) => !rcd.deactivatedAt) ?? [];
  const relationComposers =
    composerData?.relationRecordComposerDefinitions.map((rr) => rr.recordComposerDefinition) ?? [];

  const composerRefetchQuery = [
    {
      query: GetRecordComposerDefinitionsDocument,
      variables: { tenantId, dataImportProjectId: importProjectId },
    },
  ];

  return (
    <div className="min-w-[500px]">
      <form id={formId} onSubmit={handleSubmit(submit)} className="space-y-6 pt-2">
        {!hasEvidenceSchemaSet && (
          <div
            role="alert"
            className="flex items-start gap-2.5 rounded-[var(--radius-md)] bg-warning-bg p-4 text-14-regular text-warning"
          >
            <TriangleAlert aria-hidden className="mt-px size-4 shrink-0" />
            <span>{t("feature.batchChangeRequestsJobs.form.noSchemaSet")}</span>
          </div>
        )}

        <DetailSubHeader id={batchChangeRequestsJobId ?? "-"} createdAt={job?.createdAt} updatedAt={job?.updatedAt} />

        <Controller
          name="jobType"
          control={control}
          defaultValue={jobType}
          render={({ field }) => (
            <div className="space-y-2">
              <Label>{t("model.batchChangeRequestsJob.jobTypeId")}</Label>
              <Select
                value={field.value}
                onValueChange={(value) => {
                  field.onChange(value);
                  setValue("recordComposerDefinitionIds", []);
                  setJobType(value as BatchChangeRequestsJobType);
                }}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {batchChangeRequestsJobTypes.map((type) => (
                    <SelectItem key={type} value={type}>
                      <JobTypeLabel type={type} />
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        />

        {/* Historical s3_script jobs have no composer, so neither field applies to them. */}
        {(isEntity || isRelation) && (
          <BatchChangeRequestsComposerField
            key={jobType}
            control={control}
            importProjectId={importProjectId}
            label={isEntity ? t("model.entityComposer.modelName") : t("model.relationComposer.modelName")}
            options={isEntity ? entityComposers : relationComposers}
            peekType={isEntity ? "entityComposer" : "relationComposer"}
            hasEvidenceSchemaSet={hasEvidenceSchemaSet}
            defaultValue={defaultComposerIds}
            onRefetch={() => void refetchComposers()}
            onAdd={() => setAddFormOpen(true)}
            setValue={setValue}
          />
        )}

        <div className="space-y-2">
          <Label htmlFor="bcr-job-name">{t("model.common.name")}</Label>
          <Input id="bcr-job-name" {...register("name")} defaultValue={job?.name ?? ""} />
        </div>

        {!formId && (
          <div className="flex justify-end">
            <Button type="submit" disabled={!hasEvidenceSchemaSet || processing}>
              {processing && <Loader2 className="animate-spin" />}
              <span>{t("ui.save")}</span>
            </Button>
          </div>
        )}
      </form>

      <Dialog open={addFormOpen} onOpenChange={setAddFormOpen}>
        <DialogContent className="max-h-[90vh] max-w-4xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="capitalize">
              {t("ui.addX", {
                x: isEntity ? t("model.entityComposer.modelName") : t("model.relationComposer.modelName"),
              })}
            </DialogTitle>
          </DialogHeader>
          <Suspense fallback={<DetailLoading />}>
            {addFormOpen && isEntity && (
              <EntityComposerEditForm
                importProjectId={importProjectId}
                evidenceSetIds={[evidenceSetId]}
                recordComposerDefinitionId={null}
                refetchQueriesOnEdit={composerRefetchQuery}
                onSubmit={() => {
                  setAddFormOpen(false);
                  void refetchComposers();
                }}
              />
            )}
            {addFormOpen && isRelation && (
              <RelationComposerEditForm
                importProjectId={importProjectId}
                evidenceSetIds={[evidenceSetId]}
                recordComposerDefinitionId={null}
                refetchQueriesOnEdit={composerRefetchQuery}
                onSubmit={() => {
                  setAddFormOpen(false);
                  void refetchComposers();
                }}
              />
            )}
          </Suspense>
        </DialogContent>
      </Dialog>
    </div>
  );
}
